import styled from 'styled-components'
import useTranslation from '../../../hooks/useTranslation'
import useApi from '../../../hooks/useApi'
import { useQuery } from '@tanstack/react-query'
import Modal from '../../modal/Modal'

const Header = styled.div`
    padding: 1rem 1rem 0.5rem;
    font-weight: 700;
    font-size: 1rem;
    color: ${({ theme }) => theme.font.color};
    border-bottom: 1px solid ${({ theme }) => theme.modal.divider};
`

const Body = styled.div`
    padding: 0.75rem 1rem;
    overflow-y: auto;
    max-height: 50vh;
`

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 0.85rem;
`

const Th = styled.th`
    text-align: left;
    padding: 0.4rem 0.5rem;
    color: ${({ theme }) => theme.font.label};
    font-weight: 600;
    border-bottom: 1px solid ${({ theme }) => theme.form.border};
`

const Td = styled.td`
    padding: 0.4rem 0.5rem;
    color: ${({ theme }) => theme.font.color};
    border-bottom: 1px solid ${({ theme }) => theme.form.border};
`

const CurrentDot = styled.span`
    color: ${({ theme }) => theme.btn.primary};
    font-weight: 700;
    margin-left: 0.25rem;
`

const TotalRow = styled.tr`
    font-weight: 700;
    border-top: 2px solid ${({ theme }) => theme.form.border};
`

const Footer = styled.div`
    padding: 0.75rem 1rem;
    display: flex;
    justify-content: flex-end;
    border-top: 1px solid ${({ theme }) => theme.modal.divider};
`

const CloseBtn = styled.button`
    padding: 0.4rem 1.2rem;
    font-size: 0.85rem;
    font-weight: 600;
    border: none;
    border-radius: 0.375rem;
    cursor: pointer;
    background: ${({ theme }) => theme.btn.primary};
    color: ${({ theme }) => theme.btn.font};

    &:hover { opacity: 0.88; }
`

const Legend = styled.div`
    font-size: 0.72rem;
    color: ${({ theme }) => theme.font.label};
    margin-top: 0.5rem;
`

const InventoryLocationsModal = ({ itemId, itemName, currentLocationId, onClose }) => {
    const { t } = useTranslation()
    const { getMe } = useApi()

    const { data, isLoading } = useQuery({
        queryKey: ['inventoryLedger', 'byItemLocation', itemId],
        queryFn: () => getMe(`inventoryLedger/agr/byItemLocation?itemId=${itemId}`),
        enabled: !!itemId,
    })

    const { data: locationsData } = useQuery({
        queryKey: ['location'],
        queryFn: () => getMe('location'),
        staleTime: 5 * 60 * 1000,
    })

    const locationMap = Object.fromEntries(
        (locationsData?.data || []).map(loc => [loc.id, loc.location || loc.id])
    )

    const rows = (Array.isArray(data?.data) ? data.data : []).filter(r => r.ledgersum > 0)
    const total = rows.reduce((sum, r) => sum + (r.ledgersum || 0), 0)

    return (
        <Modal show onClose={onClose} setShow={(v) => { if (!v) onClose() }}>
            <Header>📦 {t('warehouseOrder.pick.inventory.title')} — {itemName}</Header>

            <Body>
                {isLoading ? (
                    <div style={{ padding: '1rem', textAlign: 'center' }}>…</div>
                ) : (
                    <>
                        <Table>
                            <thead>
                                <tr>
                                    <Th>Location</Th>
                                    <Th style={{ textAlign: 'right' }}>Qty</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, i) => (
                                    <tr key={i}>
                                        <Td>
                                            {locationMap[row.locationid] || row.locationid}
                                            {row.locationid === currentLocationId && (
                                                <CurrentDot> ●</CurrentDot>
                                            )}
                                        </Td>
                                        <Td style={{ textAlign: 'right' }}>{row.ledgersum}</Td>
                                    </tr>
                                ))}
                                <TotalRow>
                                    <Td>Total</Td>
                                    <Td style={{ textAlign: 'right' }}>{total}</Td>
                                </TotalRow>
                            </tbody>
                        </Table>
                        {rows.some(r => r.locationid === currentLocationId) && (
                            <Legend>● = current pick location</Legend>
                        )}
                    </>
                )}
            </Body>

            <Footer>
                <CloseBtn onClick={onClose}>Close</CloseBtn>
            </Footer>
        </Modal>
    )
}

export default InventoryLocationsModal
